import type { SearchableDropdownOption } from "./searchableDropdown.types";

interface KeyboardActionInput {
  readonly filteredOptions: readonly SearchableDropdownOption[];
  readonly highlightedIndex: number;
  readonly isOpen: boolean;
  readonly key: string;
  readonly query: string;
  readonly shouldSelectOnEnterOrTab: boolean;
}

export type SearchableDropdownKeyboardAction =
  | { readonly type: "none" }
  | { readonly type: "open" }
  | { readonly type: "prevent-default" }
  | {
      readonly index: number;
      readonly shouldPreventDefault: true;
      readonly type: "highlight";
    }
  | {
      readonly option: SearchableDropdownOption;
      readonly shouldPreventDefault: boolean;
      readonly type: "select";
    }
  | {
      readonly shouldPreventDefault: boolean;
      readonly type: "close";
    };

function getConfirmOption(
  input: KeyboardActionInput,
): SearchableDropdownOption | null {
  if (!input.shouldSelectOnEnterOrTab) return null;
  const highlightedOption = input.filteredOptions[input.highlightedIndex];
  if (highlightedOption) return highlightedOption;
  return input.query.trim().length > 0
    ? (input.filteredOptions[0] ?? null)
    : null;
}

function getNextHighlightIndex(input: KeyboardActionInput): number {
  if (input.filteredOptions.length === 0) return -1;
  if (input.key === "ArrowDown") {
    return input.highlightedIndex < 0
      ? 0
      : Math.min(input.highlightedIndex + 1, input.filteredOptions.length - 1);
  }
  return input.highlightedIndex < 0 ||
    input.highlightedIndex >= input.filteredOptions.length
    ? input.filteredOptions.length - 1
    : Math.max(input.highlightedIndex - 1, 0);
}

function getOptInConfirmAction(
  input: KeyboardActionInput,
): SearchableDropdownKeyboardAction | null {
  const isOptInConfirmKey = input.key === "Enter" || input.key === "Tab";
  const confirmOption = isOptInConfirmKey ? getConfirmOption(input) : null;
  return confirmOption
    ? {
        option: confirmOption,
        shouldPreventDefault: input.key === "Enter",
        type: "select",
      }
    : null;
}

function getOpenDropdownAction(
  input: KeyboardActionInput,
): SearchableDropdownKeyboardAction {
  if (input.shouldSelectOnEnterOrTab && input.key === "Tab") {
    return { shouldPreventDefault: false, type: "close" };
  }
  if (input.key === "ArrowDown" || input.key === "ArrowUp") {
    return {
      index: getNextHighlightIndex(input),
      shouldPreventDefault: true,
      type: "highlight",
    };
  }
  if (input.key === "Enter" && input.highlightedIndex >= 0) {
    const option = input.filteredOptions[input.highlightedIndex];
    if (option) {
      return { option, shouldPreventDefault: true, type: "select" };
    }
  }
  if (input.key === "Enter") return { type: "prevent-default" };
  return input.key === "Escape"
    ? { shouldPreventDefault: true, type: "close" }
    : { type: "none" };
}

export function getSearchableDropdownKeyboardAction(
  input: KeyboardActionInput,
): SearchableDropdownKeyboardAction {
  if (!input.isOpen) {
    return input.key === "ArrowDown" || input.key === "Enter"
      ? { type: "open" }
      : { type: "none" };
  }
  return getOptInConfirmAction(input) ?? getOpenDropdownAction(input);
}
